import * as Yup from 'yup';
import { emailRegEx, phoneRegEx, websiteRegex, zipCodeRegEx } from '@/utils/RegularExpressions';
import { States } from '../../../../../constants';
import {
  acquisitionTypes,
  constructionTypeOptions,
  neighborhoods,
  projectTypeOptions,
  siteControlStatuses,
} from './ProjectDetails/constants';

export const validStateOptions: string[] = States.map((state) => state.name);

export const projectByRightOptions = ['Yes', 'No', 'Not Yet Determined'];

const defaultStepsSchema = Yup.object({
  applicantInformation: Yup.object({
    fullName: Yup.string().required('Required').max(250, 'Must be less than 250 characters.'),
    contactEmail: Yup.string().required('Required').matches(emailRegEx, 'Please enter a valid email address'),
  }),
});

const proposedRent = Yup.object({
  studios: Yup.number()
    .typeError('Number is required')
    .positive('It must be greater than 0')
    .integer('It must be integer')
    .transform((_, val) => (val ? Number(val) : undefined)),
  oneBedroom: Yup.number()
    .typeError('Number is required')
    .positive('It must be greater than 0')
    .integer('It must be integer')
    .transform((_, val) => (val ? Number(val) : undefined)),
  twoBedrooms: Yup.number()
    .typeError('Number is required')
    .positive('It must be greater than 0')
    .integer('It must be integer')
    .transform((_, val) => (val ? Number(val) : undefined)),
  threeBedrooms: Yup.number()
    .typeError('Number is required')
    .positive('It must be greater than 0')
    .integer('It must be integer')
    .transform((_, val) => (val ? Number(val) : undefined)),
});
const step1Schema = Yup.object({
  projectDetails: Yup.object({
    streetAddress: Yup.string().required('Required').min(4, 'It must be greater than 4 character'),
    city: Yup.string().required('Required').min(2, 'It must be greater than 2 character'),
    state: Yup.string().required('Required').oneOf(validStateOptions, 'Invalid option'),
    zipCode: Yup.string()
      .required('Required')
      .matches(zipCodeRegEx, 'It must be 5 digits')
      .max(5, 'It must be 5 digits')
      .min(5, 'It must be 5 digits'),
    neighborhood: Yup.string().required('Required').oneOf(neighborhoods, 'Invalid option'),
    projectType: Yup.string().required('Required').oneOf(projectTypeOptions, 'Invalid option'),
    noOfCommercial: Yup.number()
      .transform((val) => (Number.isNaN(val) ? 0 : val))
      .when('projectType', {
        is: 'Mixed Use',
        then: (schema) => schema.required('Required').typeError('Must be a number'),
        otherwise: (schema) => schema.integer().nullable(),
      }),
    averageSalesPrice: Yup.number()
      .transform((val) => (Number.isNaN(val) ? 0 : val))
      .when('projectType', {
        is: 'Affordable Homeownership',
        then: (schema) => schema.required('Required').typeError('Must be a number'),
        otherwise: (schema) => schema.integer().nullable(),
      }),
    proposedUnits: Yup.number()
      .required('Required')
      .typeError('Number is required')
      .positive('It must be greater than 0')
      .integer('It must be integer')
      .transform((_, val) => (val ? Number(val) : undefined)),
    constructionType: Yup.string().required('Required').oneOf(constructionTypeOptions, 'Invalid option'),
    projectByRight: Yup.string().required('Required').oneOf(projectByRightOptions, 'Invalid option'),
    // proposedPurchasePrice: Yup.number()
    //   .required("Required")
    //   .typeError("Number is required")
    //   .positive("It must be greater than 0")
    //   .transform((_, val) => (val ? Number(val) : undefined)),
    developmentCostEstimate: Yup.number()
      .required('Required')
      .typeError('Number is required')
      .positive('It must be greater than 0')
      .transform((_, val) => (val ? Number(val) : undefined)),
    acquisitionType: Yup.string().required('Required').oneOf(acquisitionTypes, 'Invalid option'),
    siteControlStatus: Yup.string().required('Required').oneOf(siteControlStatuses, 'Invalid option'),
    haveCommunitySupport: Yup.string().required('Required'),
    isPropertyCityOwn: Yup.string().required('Required'),
    proposedAmount: Yup.number()
      .transform((val) => (Number.isNaN(val) ? 0 : val))
      .when('isPropertyCityOwn', {
        is: 'yes',
        then: (schema) => schema.required('Required').typeError('Must be a number'),
        otherwise: (schema) => schema.integer().nullable(),
      }),
    familyType: Yup.string().required('Required'),
    avgSalesPrice: Yup.number()
      .transform((val) => (Number.isNaN(val) ? 0 : val))
      .when('familyType', {
        is: 'single',
        then: (schema) => schema.required('Required').typeError('Must be a number'),
        otherwise: (schema) => schema.integer().nullable(),
      }),
    rentType: Yup.object().when('familyType', ([familyType], schema) => {
      return familyType === 'multi' ? proposedRent : schema;
    }),
    moreInfo: Yup.string().required('Required').min(4, 'It must be greater than 4 character'),
  }),
});

const step2Schema = Yup.object({
  developmentTeam: Yup.array(),
});

const step3Schema = Yup.object({
  developmentSchedule: Yup.object({
    schedule1: Yup.object({
      startDate: Yup.date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test({
          message: 'It must be earlier than finish date',
          test: (val, ctx) => {
            const { finishDate } = ctx.parent;
            if (finishDate !== null && val) {
              return new Date(val) < new Date(finishDate);
            }
            return true;
          },
        })
        .nullable(),
      finishDate: Yup.date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test({
          message: 'It must be later than start date',
          test: (val, ctx) => {
            const { startDate } = ctx.parent;
            if (startDate !== null && val) {
              return new Date(val) > new Date(startDate);
            }
            return true;
          },
        })
        .nullable(),
    }),
    schedule2: Yup.object({
      startDate: Yup.date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test({
          message: 'It must be earlier than finish date',
          test: (val, ctx) => {
            const { finishDate } = ctx.parent;
            if (finishDate !== null && val) {
              return new Date(val) < new Date(finishDate);
            }
            return true;
          },
        })
        .nullable(),
      finishDate: Yup.date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test({
          message: 'It must be later than start date',
          test: (val, ctx) => {
            const { startDate } = ctx.parent;
            if (startDate !== null && val) {
              return new Date(val) > new Date(startDate);
            }
            return true;
          },
        })
        .nullable(),
    }),
    schedule3: Yup.object({
      startDate: Yup.date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test({
          message: 'It must be earlier than finish date',
          test: (val, ctx) => {
            const { finishDate } = ctx.parent;
            if (finishDate !== null && val) {
              return new Date(val) < new Date(finishDate);
            }
            return true;
          },
        })
        .nullable(),
      finishDate: Yup.date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test({
          message: 'It must be later than start date',
          test: (val, ctx) => {
            const { startDate } = ctx.parent;
            if (startDate !== null && val) {
              return new Date(val) > new Date(startDate);
            }
            return true;
          },
        })
        .nullable(),
    }),
    schedule4: Yup.object({
      startDate: Yup.date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test({
          message: 'It must be earlier than finish date',
          test: (val, ctx) => {
            const { finishDate } = ctx.parent;
            if (finishDate !== null && val) {
              return new Date(val) < new Date(finishDate);
            }
            return true;
          },
        })
        .nullable(),
      finishDate: Yup.date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test({
          message: 'It must be later than start date',
          test: (val, ctx) => {
            const { startDate } = ctx.parent;
            if (startDate !== null && val) {
              return new Date(val) > new Date(startDate);
            }
            return true;
          },
        })
        .nullable(),
    }),
    schedule5: Yup.object({
      startDate: Yup.date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test({
          message: 'It must be earlier than finish date',
          test: (val, ctx) => {
            const { finishDate } = ctx.parent;
            if (finishDate !== null && val) {
              return new Date(val) < new Date(finishDate);
            }
            return true;
          },
        })
        .nullable(),
      finishDate: Yup.date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .test({
          message: 'It must be later than start date',
          test: (val, ctx) => {
            const { startDate } = ctx.parent;
            if (startDate !== null && val) {
              return new Date(val) > new Date(startDate);
            }
            return true;
          },
        })
        .nullable(),
    }),
  }),
});

const step4Schema = Yup.object({
  projectFinance: Yup.object({
    sources: Yup.array().of(
      Yup.object({
        name: Yup.string().required('This is required'),
        amount: Yup.number()
          .min(0, 'Amount must be 0 or greater')
          .when('name', {
            is: 'Developer Equity',
            then: (schema) => schema.required('Required'),
            otherwise: (schema) => schema.nullable(),
          }),
      })
    ),
    uses: Yup.object({
      acquisition: Yup.number()
        .typeError('Provide number')
        .positive('It must be greater than 0')
        .transform((_, val) => (val ? Number(val) : undefined)),
      hardCost: Yup.number()
        .typeError('Provide number')
        .positive('It must be greater than 0')
        .transform((_, val) => (val ? Number(val) : undefined)),
      softCost: Yup.number()
        .typeError('Provide number')
        .positive('It must be greater than 0')
        .transform((_, val) => (val ? Number(val) : undefined)),
      financeCost: Yup.number()
        .typeError('Provide number')
        .positive('It must be greater than 0')
        .transform((_, val) => (val ? Number(val) : undefined)),
      developerFeeAmount: Yup.number()
        .typeError('Provide number')
        .positive('It must be greater than 0')
        .transform((_, val) => (val ? Number(val) : undefined)),
    }).test('is-equal', 'Total Uses is not equal to total Sources', (value, { parent: { sources }, createError }) => {
      const totalUsesSum = Object.values(value || {}).reduce((partialSum = 0, a = 0) => partialSum + a, 0);
      const totalSources = (sources as []).reduce<number>((partialSum = 0, a: any) => partialSum + ((a?.amount as number) || 0), 0);
      const isEqual = totalUsesSum === totalSources;
      if (isEqual) {
        return isEqual;
      } else {
        return createError({
          path: 'projectFinance',
          message: isEqual ? undefined : 'Sources and Uses must be equal',
        });
      }
    }),
    willUseHomeFunds: Yup.string().required('Required'),
    willUseCDBGFunds: Yup.string().required('Required'),
  }),
});

const step5Schema = Yup.object({
  applicantInfo: Yup.object({
    entityName: Yup.string().required('Required').min(2, 'It must be greater than 2 characters'),
    contactPersonName: Yup.string().required('Required').min(2, 'It must be greater than 2 characters'),
    contactPersonEmail: Yup.string().required('Required').matches(emailRegEx, 'Invalid E-mail Address'),
    streetAddress: Yup.string().required('Required').min(4, 'It must be greater than 4 character'),
    city: Yup.string().required('Required').min(2, 'It must be greater than 2 character'),
    state: Yup.string().required('Required').oneOf(validStateOptions, 'Invalid option'),
    zipCode: Yup.string()
      .required('Required')
      .matches(zipCodeRegEx, 'It must be 5 digits')
      .max(5, 'It must be 5 digits')
      .min(5, 'It must be 5 digits'),
    phone: Yup.string()
      .required('Required')
      .typeError('Number is required')
      .matches(phoneRegEx, 'It must be 10 digits')
      .min(10, 'It must be 10 digits')
      .max(10, 'It must be 10 digits'),
    website: Yup.string()
      .matches(websiteRegex, 'Invalid website format')
      .nullable()
      .transform((v) => (v === '' ? null : v)),
    formationDate: Yup.date().required('Required').typeError('Invalid date').max(new Date(), 'It cannot be in future'),
    fullTimeEmployeeCount: Yup.number()
      .required('Required')
      .typeError('Number is required')
      .integer('It must be integer')
      .positive('It cannot be negative')
      .min(0, 'It must be greater than 0'),
  }),
});

const _step6Schema = Yup.object({
  leadershipInformation: Yup.object({
    racialEthnicGroup: Yup.array().min(1, 'At least one option should be selected'),
    hasMajorityOwnership: Yup.string().required('Required'),
    majorityOwnership: Yup.array().when('hasMajorityOwnership', {
      is: 'yes',
      then: (schema) => schema.min(1, 'At least one option should be selected'),
      otherwise: (schema) => schema.optional(),
    }),
    boardOfDirectorsPercent: Yup.object({
      communityMembersPercent: Yup.number()
        .min(1, 'It must be greater than 0')
        .max(100, 'It must be less than or equal to 100')
        .nullable()
        .transform((_, val) => (val ? Number(val) : null)),
      hispanicLatinxPercent: Yup.number()
        .min(1, 'It must be greater than 0')
        .max(100, 'It must be less than or equal to 100')
        .nullable()
        .transform((_, val) => (val ? Number(val) : null)),
      blackAfricanAmericanPercent: Yup.number()
        .min(1, 'It must be greater than 0')
        .max(100, 'It must be less than or equal to 100')
        .nullable()
        .transform((_, val) => (val ? Number(val) : null)),
      asianPacificIslandersPercent: Yup.number()
        .min(1, 'It must be greater than 0')
        .max(100, 'It must be less than or equal to 100')
        .nullable()
        .transform((_, val) => (val ? Number(val) : null)),
      nativeAmericanPercent: Yup.number()
        .min(1, 'It must be greater than 0')
        .max(100, 'It must be less than or equal to 100')
        .nullable()
        .transform((_, val) => (val ? Number(val) : null)),
      whiteCaucasianPercent: Yup.number()
        .min(1, 'It must be greater than 0')
        .max(100, 'It must be less than or equal to 100')
        .nullable()
        .transform((_, val) => (val ? Number(val) : null)),
      multiRacialPercent: Yup.number()
        .min(1, 'It must be greater than 0')
        .max(100, 'It must be less than or equal to 100')
        .nullable()
        .transform((_, val) => (val ? Number(val) : null)),
    }).test({
      message: 'Total must be less than or equal to 100%',
      test: (val) => {
        let total = Number(val.communityMembersPercent);
        total += Number(val.hispanicLatinxPercent);
        total += Number(val.blackAfricanAmericanPercent);
        total += Number(val.asianPacificIslandersPercent);
        total += Number(val.nativeAmericanPercent);
        total += Number(val.whiteCaucasianPercent);
        total += Number(val.multiRacialPercent);

        return total <= 100;
      },
    }),
  }),
});

const step7Schema = Yup.object({
  experience: Yup.object({
    companyCompleted: Yup.array(),
    principalCompleted: Yup.array(),
  }),
});

export const BDCDPPValidationSchema = [defaultStepsSchema, step1Schema, step2Schema, step3Schema, step4Schema, step5Schema, step7Schema];
